import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ClosePatientTaskRequestSchema, type ClosePatientTaskRequest, type PatientTaskDto } from '@ctms/contracts';
import { z } from 'zod';
import { AlertCircle, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { PersonnelSelect } from '../../trials/personnel-select/personnel-select.js';
import { visitsApi } from '../visits.api.js';

/**
 * The contract of `PATCH /visits/patient-tasks/:id/close` asks only for the
 * person who closed the task (`stopPersonnelId`) — the server stamps the closing
 * date. The mockup has no closing dialog, so this small confirmation mirrors the
 * creation dialog: the highlighted row is closed for the picked person
 * (interpretation of the session-4 handoff, documented in `docs/`).
 */
const CloseTaskFormSchema = z.object({
  stopPersonnelId: z.string().min(1, 'Wybierz osobę.'),
});

type CloseTaskFormInput = z.infer<typeof CloseTaskFormSchema>;

export interface CloseTaskDialogProps {
  /** The task highlighted in the list — closing is a per-task action (REQ-VIS-051). */
  task: PatientTaskDto;
  /** Called after the server closed the task — the caller refreshes the list. */
  onSaved: () => void;
  onClose: () => void;
}


/**
 * "Zamknij wybrane" — closes exactly the highlighted open task (OPCJA 2A:
 * a single highlighted row, no checkbox column). The task leaves the open list
 * because the server stamps its `stopDate` (REQ-VIS-051).
 */
export function CloseTaskDialog({ task, onSaved, onClose }: CloseTaskDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CloseTaskFormInput>({
    resolver: zodResolver(CloseTaskFormSchema),
    defaultValues: { stopPersonnelId: '' },
  });

  const closeMutation = useMutation({
    mutationFn: (request: ClosePatientTaskRequest) => visitsApi.closePatientTask(task.id, request),
    onSuccess: () => {
      onSaved();
    },
    onError: async (err: unknown) => {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zamknąć zadania.'));
    },
  });

  const isSubmitting = closeMutation.isPending;

  const onSubmit = (input: CloseTaskFormInput): void => {
    setErrorMessage(null);
    const parsed = ClosePatientTaskRequestSchema.safeParse({
      stopPersonnelId: Number(input.stopPersonnelId),
    });
    if (!parsed.success) {
      setErrorMessage('Nie udało się zamknąć zadania.');
      return;
    }
    closeMutation.mutate(parsed.data);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="close-task-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <h2 id="close-task-title" className="text-base font-semibold text-slate-100">
            Zamknij zadanie
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-4 text-xs text-slate-300">
          Zadanie: <span className="font-medium text-slate-100">{task.name}</span>
        </p>

        <form
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          className="mt-5 space-y-4"
          noValidate
        >
          {errorMessage !== null && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-xs text-rose-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <PersonnelSelect id="close-task-person" registration={register('stopPersonnelId')} disabled={isSubmitting} />
            {errors.stopPersonnelId !== undefined && (
              <p className="mt-1 text-xs text-rose-400">{errors.stopPersonnelId.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button
              type="button"
              id="cancel-close-task"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              id="save-close-task"
              disabled={isSubmitting}
              className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Zamykanie…' : 'Zamknij zadanie'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}